import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Col,
    Descriptions,
    Empty,
    Progress,
    Row,
    Space,
    Spin,
    Tag,
    Typography,
    message
} from "antd";
import {
    ArrowLeftOutlined,
    BankOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    FileTextOutlined,
    HomeOutlined,
    PrinterOutlined,
    SafetyCertificateOutlined,
    TeamOutlined,
    UserOutlined,
    WalletOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useHistory, useParams } from "react-router-dom";
import axiosinstance from "util/Api";
import { CisUI } from "../../../../util/CISUI";

const { Title, Text, Paragraph } = Typography;

/* ============================================================
 * HELPERS
 * ============================================================ */

const normalize = (v) => String(v || "").trim().toUpperCase();

const display = (v) =>
    v === null || v === undefined || v === "" ? "-" : v;

const money = (v) =>
    Number(v || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

const pretty = (v) =>
    String(v || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (x) => x.toUpperCase());

const jsonObject = (v) => {
    if (!v) return {};
    if (typeof v === "object") return v;

    try {
        return JSON.parse(v);
    } catch {
        return {};
    }
};

const LEVEL_LABELS = {
    LOCAL: "Local",
    DISTRICT: "District",
    REGION: "Area",
    AREA: "Area",
    NATIONAL: "National"
};

const STATUS_COLORS = {
    DRAFT: "default",
    SUBMITTED: "blue",
    RESUBMITTED: "cyan",
    UNDER_REVIEW: "processing",
    FORWARDED: "geekblue",
    RETURNED: "orange",
    APPROVED: "green",
    REJECTED: "red",
    CANCELLED: "default"
};

const PRIORITY_COLORS = {
    CRITICAL: "red",
    URGENT: "volcano",
    HIGH: "orange",
    MEDIUM: "blue",
    LOW: "default"
};

const COLORS = {
    navy: "#163B63",
    navyDark: "#102E50",
    blue: "#2B6EA6",
    blueLight: "#EEF5FB",
    green: "#237A57",
    greenLight: "#EEF8F3",
    orange: "#C87516",
    orangeLight: "#FFF7EA",
    red: "#B84545",
    text: "#1B2B38",
    secondary: "#667685",
    border: "#DCE4EB",
    lightBorder: "#E9EEF3",
    background: "#EEF2F6",
    white: "#FFFFFF",
    light: "#F8FAFC"
};

/* ============================================================
 * PRINT SECTION
 * ============================================================ */

const PrintSection = ({
    title,
    subtitle,
    icon,
    children,
    breakBefore = false
}) => (
    <section
        className={`report-section ${
    breakBefore ? "page-break-before" : ""
}`}
    >
        <div className="report-section-title">
            <div className="section-icon">{icon}</div>

            <div>
                <div className="section-name">{title}</div>

                {subtitle ? (
                    <div className="section-subtitle">{subtitle}</div>
                ) : null}
            </div>
        </div>

        <div className="section-content">{children}</div>
    </section>
);

/* ============================================================
 * LABEL / VALUE
 * ============================================================ */

const Field = ({ label, value, strong = false, span = false }) => (
    <div className={`field ${span ? "field-span" : ""}`}>
        <div className="field-label">{label}</div>

        <div className={`field-value ${strong ? "strong" : ""}`}>
            {display(value)}
        </div>
    </div>
);

/* ============================================================
 * JSON GRID
 * ============================================================ */

const JsonGrid = ({ data, moneyKeys = [] }) => {
    const entries = Object.entries(data || {}).filter(
        ([key]) => !key.endsWith("_explanation")
    );

    if (!entries.length) {
        return <div className="empty-info">No information available.</div>;
    }

    return (
        <div className="field-grid">
            {entries.map(([key, value]) => (
                <Field
                    key={key}
                    label={pretty(key)}
                    value={
                        moneyKeys.includes(key)
                            ? money(value)
                            : display(value)
                    }
                />
            ))}
        </div>
    );
};

/* ============================================================
 * KPI
 * ============================================================ */

const KPI = ({
    label,
    value,
    helper,
    icon,
    variant = "blue"
}) => (
    <div className={`kpi kpi-${variant}`}>
        <div className="kpi-top">
            <span className="kpi-label">{label}</span>

            <span className="kpi-icon">{icon}</span>
        </div>

        <div className="kpi-value">{value}</div>

        {helper ? <div className="kpi-helper">{helper}</div> : null}
    </div>
);

/* ============================================================
 * MAIN
 * ============================================================ */

export default function AssetRequestPrint() {
    const { id } = useParams();
    const history = useHistory();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);

            const res = await axiosinstance.get(
                `/assets/assetrequestopen/${id}`
            );

            if (res.data.status !== 1) {
                message.warning(
                    res.data.msg || "Unable to load asset request."
                );
                return;
            }

            setData(res.data.data || null);
        } catch (e) {
            message.error(
                e?.response?.data?.msg ||
                    e?.response?.data?.message ||
                    "Unable to load asset request."
            );
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    /* ========================================================
     * LOADING
     * ======================================================== */

    if (loading) {
        return (
            <div className="loading-page">
                <Spin size="large" />
                <Text type="secondary">
                    Preparing printable asset request...
                </Text>
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ paddingTop: 100 }}>
                <Empty description="Asset request not found" />
            </div>
        );
    }

    /* ========================================================
     * DATA
     * ======================================================== */

    const specs = jsonObject(data.asset_specifications);
    const futureCosts = jsonObject(data.future_costs);
    const income = jsonObject(data.income_potential);
    const checklist = jsonObject(data.financial_checklist);
    const risks = jsonObject(data.risk_assessment);

    const workflow = data.workflow || [];
    const documents = data.documents || [];

    const cost = Number(data.estimated_cost || 0);

    const available =
        Number(data.amount_available || 0) +
        Number(data.emergency_fund_available || 0) +
        Number(data.building_fund_available || 0);

    const fundingGap = Math.max(cost - available, 0);

    const fundingPct =
        cost > 0
            ? Math.min(Math.round((available / cost) * 100), 100)
            : 0;

    const org = {
        company:
            data.company_name ||
            data.company?.name ||
            data.com_name ||
            (data.com_id ? `Company #${data.com_id}` : "-"),

        nation:
            data.nation_name ||
            data.nation?.name ||
            (data.nation_id ? `Nation #${data.nation_id}` : "-"),

        region:
            data.region_name ||
            data.region?.name ||
            (data.region_id ? `Region #${data.region_id}` : "-"),

        district:
            data.district_name ||
            data.district?.name ||
            (data.district_id ? `District #${data.district_id}` : "-"),

        local:
            data.local_name ||
            data.local?.name ||
            (data.local_id ? `Local #${data.local_id}` : "-")
    };

    const approvedWorkflow = workflow.filter(
        (x) => normalize(x.action) === "APPROVED"
    );

    const lastWorkflow =
        workflow.length > 0 ? workflow[workflow.length - 1] : null;

    /* ========================================================
     * VIEW
     * ======================================================== */

    return (
        <div className="asset-print-page">

            {/* =================================================
                CSS
            ================================================= */}

            <style>{`

* {
    box-sizing: border-box;
}

body {
    margin: 0;
    background: ${COLORS.background};
}

.asset-print-page {
    min-height: 100vh;
    padding: 24px;
    color: ${COLORS.text};
    font-family:
    Inter,
    -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        Arial,
    sans-serif;
}

/* =============================================
   TOOLBAR
============================================= */

.print-toolbar {
    max-width: 210mm;
    margin: 0 auto 16px auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.toolbar-title {
    font-size: 13px;
    color: ${COLORS.secondary};
}

/* =============================================
   A4 PAPER
============================================= */

.print-paper {
    width: 210mm;
    max-width: 100%;
    min-height: 297mm;
    margin: 0 auto;
    background: #fff;
    box-shadow:
    0 8px 30px rgba(16, 46, 80, 0.10);
}

.report-body {
    padding:
        14mm
    14mm
    12mm
    14mm;
}

/* =============================================
   HEADER
============================================= */

.report-header {
    position: relative;
    overflow: hidden;
    background:
        linear-gradient(
            120deg,
        ${COLORS.navyDark} 0%,
        ${COLORS.navy} 58%,
#245B8D 100%
);
    color: white;
    padding:
        12mm
    14mm
    10mm
    14mm;
}

.report-header:after {
    content: "";
    position: absolute;
    width: 210px;
    height: 210px;
    border-radius: 50%;
    right: -85px;
    top: -105px;
    border: 40px solid rgba(255,255,255,.045);
}

.report-header:before {
    content: "";
    position: absolute;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    right: 40px;
    bottom: -110px;
    border: 30px solid rgba(255,255,255,.035);
}

.header-content {
    position: relative;
    z-index: 2;
}

.org-row {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    align-items: flex-start;
}

.org-identity {
    display: flex;
    align-items: center;
    gap: 12px;
}

.org-logo-placeholder {
    width: 46px;
    height: 46px;
    border-radius: 10px;
    background: rgba(255,255,255,.14);
    border: 1px solid rgba(255,255,255,.20);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
}

.org-name {
    font-size: 17px;
    line-height: 1.25;
    font-weight: 700;
    color: #fff;
}

.org-sub {
    font-size: 10px;
    opacity: .75;
    margin-top: 3px;
}

.report-type {
    text-align: right;
}

.report-type-label {
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    opacity: .65;
}

.report-type-title {
    font-size: 15px;
    font-weight: 650;
    margin-top: 3px;
}

.request-header {
    margin-top: 22px;
    padding-top: 17px;
    border-top: 1px solid rgba(255,255,255,.14);
    display: flex;
    justify-content: space-between;
    gap: 20px;
    align-items: flex-end;
}

.request-label {
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 1.3px;
    opacity: .65;
}

.request-number {
    font-size: 25px;
    line-height: 1.2;
    font-weight: 750;
    margin-top: 3px;
    letter-spacing: -.3px;
}

.asset-name {
    margin-top: 4px;
    font-size: 11px;
    opacity: .80;
}

.header-tags {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
    flex-wrap: wrap;
}

.header-pill {
    padding: 5px 10px;
    border-radius: 20px;
    background: rgba(255,255,255,.12);
    border: 1px solid rgba(255,255,255,.16);
    font-size: 9px;
    white-space: nowrap;
}

/* =============================================
   META STRIP
============================================= */

.meta-strip {
    background: ${COLORS.light};
    border-bottom: 1px solid ${COLORS.border};
    padding: 9px 14mm;
    display: grid;
    grid-template-columns:
    repeat(4, minmax(0, 1fr));
    gap: 14px;
}

.meta-label {
    font-size: 8px;
    text-transform: uppercase;
    color: ${COLORS.secondary};
    letter-spacing: .5px;
}

.meta-value {
    margin-top: 2px;
    font-size: 10px;
    font-weight: 600;
    color: ${COLORS.text};
}

/* =============================================
   KPI
============================================= */

.kpi-grid {
    display: grid;
    grid-template-columns:
    repeat(4, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 14px;
}

.kpi {
    padding: 11px;
    border-radius: 8px;
    border: 1px solid ${COLORS.border};
    min-height: 82px;
}

.kpi-blue {
    background: ${COLORS.blueLight};
}

.kpi-green {
    background: ${COLORS.greenLight};
}

.kpi-orange {
    background: ${COLORS.orangeLight};
}

.kpi-neutral {
    background: #F7F9FB;
}

.kpi-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 5px;
}

.kpi-label {
    font-size: 8px;
    color: ${COLORS.secondary};
    text-transform: uppercase;
    letter-spacing: .4px;
}

.kpi-icon {
    font-size: 13px;
    color: ${COLORS.navy};
}

.kpi-value {
    margin-top: 7px;
    font-size: 16px;
    line-height: 1.15;
    font-weight: 750;
    color: ${COLORS.navyDark};
}

.kpi-helper {
    margin-top: 4px;
    font-size: 8px;
    color: ${COLORS.secondary};
}

/* =============================================
   COVERAGE
============================================= */

.coverage-panel {
    margin-bottom: 15px;
    padding: 11px 13px;
    border: 1px solid ${COLORS.border};
    border-radius: 8px;
    display: grid;
    grid-template-columns:
    1fr auto;
    align-items: center;
    gap: 20px;
}

.coverage-title {
    font-size: 10px;
    font-weight: 650;
}

.coverage-subtitle {
    margin-top: 2px;
    font-size: 8px;
    color: ${COLORS.secondary};
}

.coverage-percent {
    font-size: 18px;
    font-weight: 750;
    color: ${COLORS.navy};
}

.coverage-progress {
    grid-column: 1 / -1;
    height: 6px;
    border-radius: 10px;
    overflow: hidden;
    background: #E8EDF2;
}

.coverage-progress-value {
    height: 100%;
    background: ${COLORS.green};
    border-radius: inherit;
}

/* =============================================
   SECTION
============================================= */

.report-section {
    margin-top: 15px;
    break-inside: auto;
}

.report-section-title {
    min-height: 36px;
    display: flex;
    align-items: center;
    gap: 9px;
    padding-bottom: 7px;
    border-bottom: 1.5px solid ${COLORS.navy};
    margin-bottom: 8px;
}

.section-icon {
    width: 27px;
    height: 27px;
    border-radius: 6px;
    background: ${COLORS.blueLight};
    color: ${COLORS.navy};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
}

.section-name {
    font-size: 11px;
    line-height: 1.2;
    font-weight: 700;
    color: ${COLORS.navyDark};
}

.section-subtitle {
    margin-top: 2px;
    font-size: 8px;
    color: ${COLORS.secondary};
}

.section-content {
    break-inside: auto;
}

/* =============================================
   FIELDS
============================================= */

.field-grid {
    display: grid;
    grid-template-columns:
    repeat(3, minmax(0, 1fr));
    border-left: 1px solid ${COLORS.border};
    border-top: 1px solid ${COLORS.border};
}

.field {
    padding: 7px 9px;
    min-height: 45px;
    border-right: 1px solid ${COLORS.border};
    border-bottom: 1px solid ${COLORS.border};
    break-inside: avoid;
}

.field-span {
    grid-column: 1 / -1;
}

.field-label {
    font-size: 8px;
    line-height: 1.2;
    color: ${COLORS.secondary};
    margin-bottom: 4px;
}

.field-value {
    font-size: 9.5px;
    line-height: 1.45;
    color: ${COLORS.text};
    overflow-wrap: anywhere;
}

.field-value.strong {
    font-weight: 650;
    color: ${COLORS.navyDark};
}

.empty-info {
    padding: 15px;
    border: 1px dashed ${COLORS.border};
    text-align: center;
    color: ${COLORS.secondary};
    font-size: 9px;
}

/* =============================================
   WORKFLOW
============================================= */

.workflow-list {
    position: relative;
}

.workflow-item {
    display: grid;
    grid-template-columns:
    20px minmax(0, 1fr) 125px;
    gap: 9px;
    position: relative;
    padding-bottom: 11px;
    break-inside: avoid;
}

.workflow-item:not(:last-child):before {
    content: "";
    position: absolute;
    left: 9px;
    top: 18px;
    bottom: -2px;
    width: 1px;
    background: ${COLORS.border};
}

.workflow-dot {
    width: 19px;
    height: 19px;
    border-radius: 50%;
    background: ${COLORS.blueLight};
    color: ${COLORS.navy};
    border: 1px solid #C8D9E7;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    z-index: 2;
}

.workflow-main {
    padding: 7px 9px;
    background: ${COLORS.light};
    border: 1px solid ${COLORS.lightBorder};
    border-radius: 6px;
}

.workflow-title {
    font-size: 9px;
    font-weight: 700;
    color: ${COLORS.navyDark};
}

.workflow-level {
    font-weight: 400;
    color: ${COLORS.secondary};
    margin-left: 5px;
}

.workflow-comment {
    font-size: 8.5px;
    line-height: 1.45;
    margin-top: 4px;
    color: ${COLORS.text};
}

.workflow-user {
    padding-top: 5px;
    text-align: right;
}

.workflow-user-name {
    font-size: 8.5px;
    font-weight: 650;
}

.workflow-date {
    font-size: 7.5px;
    margin-top: 2px;
    color: ${COLORS.secondary};
}

/* =============================================
   DOCUMENTS
============================================= */

.document-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 8.5px;
}

.document-table th {
    text-align: left;
    padding: 6px 8px;
    background: ${COLORS.light};
    color: ${COLORS.secondary};
    border: 1px solid ${COLORS.border};
    font-size: 7.5px;
    text-transform: uppercase;
}

.document-table td {
    padding: 6px 8px;
    border: 1px solid ${COLORS.border};
    vertical-align: top;
}

/* =============================================
   SIGNATURES
============================================= */

.signature-grid {
    display: grid;
    grid-template-columns:
    repeat(3, minmax(0, 1fr));
    gap: 18px;
    margin-top: 24px;
}

.signature {
    padding-top: 25px;
    text-align: center;
}

.signature-line {
    border-top: 1px solid #81909C;
    padding-top: 5px;
}

.signature-role {
    font-size: 8.5px;
    font-weight: 650;
    color: ${COLORS.navyDark};
}

.signature-help {
    font-size: 7.5px;
    color: ${COLORS.secondary};
    margin-top: 2px;
}

/* =============================================
   FOOTER
============================================= */

.report-footer {
    margin-top: 22px;
    border-top: 1px solid ${COLORS.border};
    padding-top: 7px;
    display: flex;
    justify-content: space-between;
    gap: 15px;
    font-size: 7px;
    color: #8A98A5;
}

.confidential {
    text-transform: uppercase;
    letter-spacing: .5px;
}

/* =============================================
   RESPONSIVE
============================================= */

@media screen and (max-width: 850px) {

.asset-print-page {
        padding: 10px;
    }

.print-paper {
        width: 100%;
    }

.kpi-grid {
        grid-template-columns:
        repeat(2, minmax(0, 1fr));
    }

.field-grid {
        grid-template-columns:
        repeat(2, minmax(0, 1fr));
    }

.workflow-item {
        grid-template-columns:
        20px minmax(0, 1fr);
    }

.workflow-user {
        grid-column: 2;
        text-align: left;
    }

}

/* =============================================
   PRINT
============================================= */

@media print {

    html,
        body {
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

.asset-print-page {
        padding: 0 !important;
        margin: 0 !important;
        background: #fff !important;
    }

.no-print {
        display: none !important;
    }

.print-paper {
        width: 100% !important;
        max-width: none !important;
        min-height: 0 !important;
        margin: 0 !important;
        box-shadow: none !important;
    }

.report-header {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

.kpi,
.field,
.workflow-item,
.signature {
        break-inside: avoid;
        page-break-inside: avoid;
    }

.report-section-title {
        break-after: avoid;
        page-break-after: avoid;
    }

.page-break-before {
        break-before: page;
        page-break-before: always;
    }

    a {
        color: inherit !important;
        text-decoration: none !important;
    }

@page {
        size: A4 portrait;
        margin: 8mm;
    }
}

`}</style>

            {/* =================================================
                TOOLBAR
            ================================================= */}

            <div className="print-toolbar no-print">
                <div>
                    <Text strong>Asset Request Print Preview</Text>

                    <div className="toolbar-title">
                        Review the document before printing or saving as PDF.
                    </div>
                </div>

                <Space>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => history.goBack()}
                    >
                        Back
                    </Button>

                    <Button
                        type="primary"
                        icon={<PrinterOutlined />}
                        onClick={() => window.print()}
                        style={{
                            background: COLORS.navy,
                            borderColor: COLORS.navy
                        }}
                    >
                        Print / Save PDF
                    </Button>
                </Space>
            </div>

            {/* =================================================
                PAPER
            ================================================= */}

            <main className="print-paper">

                {/* =============================================
                    DOCUMENT HEADER
                ============================================= */}

                <header className="report-header">

                    <div className="header-content">

                        <div className="org-row">

                            <div className="org-identity">

                                <div className="org-logo-placeholder">
                                    <HomeOutlined />
                                </div>

                                <div>
                                    <div className="org-name">
                                        {org.company}
                                    </div>

                                    <div className="org-sub">
                                        Asset Management & Approval System
                                    </div>
                                </div>

                            </div>

                            <div className="report-type">
                                <div className="report-type-label">
                                    Official Document
                                </div>

                                <div className="report-type-title">
                                    Asset Request
                                </div>
                            </div>

                        </div>

                        <div className="request-header">

                            <div>
                                <div className="request-label">
                                    Request Reference
                                </div>

                                <div className="request-number">
                                    {data.request_no ||
                                        `Asset Request #${data.id}`}
                                </div>

                                <div className="asset-name">
                                    {display(data.asset_type)}
                                    {" • "}
                                    {display(data.asset_description)}
                                </div>
                            </div>

                            <div className="header-tags">

                                <div className="header-pill">
                                    {pretty(data.status)}
                                </div>

                                <div className="header-pill">
                                    {display(data.priority_level)} Priority
                                </div>

                                <div className="header-pill">
                                    {LEVEL_LABELS[
                                        normalize(data.current_level)
                                    ] || display(data.current_level)}{" "}
                                    Level
                                </div>

                            </div>

                        </div>

                    </div>

                </header>

                {/* =============================================
                    META
                ============================================= */}

                <div className="meta-strip">

                    <div>
                        <div className="meta-label">
                            Request Date
                        </div>

                        <div className="meta-value">
                            {data.request_date
                                ? dayjs(data.request_date).format(
                                      CisUI().dateFormat
                                  )
                                : "-"}
                        </div>
                    </div>

                    <div>
                        <div className="meta-label">
                            Required By
                        </div>

                        <div className="meta-value">
                            {data.required_by_date
                                ? dayjs(
                                      data.required_by_date
                                  ).format(CisUI().dateFormat)
                                : "-"}
                        </div>
                    </div>

                    <div>
                        <div className="meta-label">
                            Local Entity
                        </div>

                        <div className="meta-value">
                            {org.local}
                        </div>
                    </div>

                    <div>
                        <div className="meta-label">
                            District
                        </div>

                        <div className="meta-value">
                            {org.district}
                        </div>
                    </div>

                </div>

                {/* =============================================
                    BODY
                ============================================= */}

                <div className="report-body">

                    {/* =========================================
                        EXECUTIVE SUMMARY
                    ========================================= */}

                    <div className="kpi-grid">

                        <KPI
                            label="Estimated Cost"
                            value={money(cost)}
                            helper="Requested investment"
                            icon={<DollarOutlined />}
                            variant="blue"
                        />

                        <KPI
                            label="Available Funds"
                            value={money(available)}
                            helper="Available funding"
                            icon={<WalletOutlined />}
                            variant="green"
                        />

                        <KPI
                            label="Funding Gap"
                            value={money(fundingGap)}
                            helper={
                                fundingGap > 0
                                    ? "Additional funding required"
                                    : "Fully funded"
                            }
                            icon={<BankOutlined />}
                            variant={
                                fundingGap > 0 ? "orange" : "green"
                            }
                        />

                        <KPI
                            label="Adult Members"
                            value={Number(
                                data.adult_membership || 0
                            ).toLocaleString()}
                            helper={`${Number(
    data.tithe_paying_members || 0
).toLocaleString()} tithe paying`}
                            icon={<TeamOutlined />}
                            variant="neutral"
                        />

                    </div>

                    {/* =========================================
                        FUNDING COVERAGE
                    ========================================= */}

                    <div className="coverage-panel">

                        <div>
                            <div className="coverage-title">
                                Funding Coverage
                            </div>

                            <div className="coverage-subtitle">
                                Available funds compared with the estimated
                                acquisition cost.
                            </div>
                        </div>

                        <div className="coverage-percent">
                            {fundingPct}%
                        </div>

                        <div className="coverage-progress">
                            <div
                                className="coverage-progress-value"
                                style={{
                                    width: `${fundingPct}%`
                                }}
                            />
                        </div>

                    </div>

                    {/* =========================================
                        ORGANIZATION
                    ========================================= */}

                    <PrintSection
                        title="Entity & Ownership"
                        subtitle="Organizational ownership and jurisdiction"
                        icon={<HomeOutlined />}
                    >
                        <div className="field-grid">

                            <Field
                                label="Company"
                                value={org.company}
                                strong
                            />

                            <Field
                                label="Nation"
                                value={org.nation}
                            />

                            <Field
                                label="Area"
                                value={org.region}
                            />

                            <Field
                                label="District"
                                value={org.district}
                            />

                            <Field
                                label="Local"
                                value={org.local}
                            />

                            <Field
                                label="Current Approval Level"
                                value={
                                    LEVEL_LABELS[
                                        normalize(
                                            data.current_level
                                        )
                                    ] ||
                                    display(data.current_level)
                                }
                                strong
                            />

                        </div>
                    </PrintSection>

                    {/* =========================================
                        REQUEST
                    ========================================= */}

                    <PrintSection
                        title="Request Information"
                        subtitle="Core request and acquisition information"
                        icon={<FileTextOutlined />}
                    >
                        <div className="field-grid">

                            <Field
                                label="Request No."
                                value={data.request_no}
                                strong
                            />

                            <Field
                                label="Asset Type"
                                value={data.asset_type}
                            />

                            <Field
                                label="Quantity"
                                value={data.quantity}
                            />

                            <Field
                                label="Priority"
                                value={data.priority_level}
                            />

                            <Field
                                label="Acquisition Method"
                                value={data.acquisition_method}
                            />

                            <Field
                                label="Estimated Cost"
                                value={money(
                                    data.estimated_cost
                                )}
                                strong
                            />

                            <Field
                                label="National Assistance"
                                value={
                                    Number(
                                        data.require_assistance_from_nation
                                    ) === 1
                                        ? "Required"
                                        : "Not Required"
                                }
                            />

                            <Field
                                label="Request Date"
                                value={
                                    data.request_date
                                        ? dayjs(
                                              data.request_date
                                          ).format(
                                              CisUI().dateFormat
                                          )
                                        : "-"
                                }
                            />

                            <Field
                                label="Required By"
                                value={
                                    data.required_by_date
                                        ? dayjs(
                                              data.required_by_date
                                          ).format(
                                              CisUI().dateFormat
                                          )
                                        : "-"
                                }
                            />

                            <Field
                                label="Justification"
                                value={data.justification}
                                span
                            />

                            <Field
                                label="Description"
                                value={data.asset_description}
                                span
                            />

                        </div>
                    </PrintSection>

                    {/* =========================================
                        SPECIFICATION
                    ========================================= */}

                    <PrintSection
                        title="Asset Specifications"
                        subtitle="Technical and asset-specific information"
                        icon={<SafetyCertificateOutlined />}
                    >
                        <JsonGrid
                            data={specs}
                            moneyKeys={[
                                "property_value",
                                "asset_value",
                                "unit_cost",
                                "current_book_value"
                            ]}
                        />
                    </PrintSection>

                    {/* =========================================
                        FUNDING
                    ========================================= */}

                    <PrintSection
                        title="Funding & Financing"
                        subtitle="Funding availability and financing structure"
                        icon={<BankOutlined />}
                    >
                        <div className="field-grid">

                            <Field
                                label="Funding Source"
                                value={data.funding_source}
                            />

                            <Field
                                label="Amount Available"
                                value={money(
                                    data.amount_available
                                )}
                                strong
                            />

                            <Field
                                label="Payment Mode"
                                value={data.payment_mode}
                            />

                            <Field
                                label="Anticipated Amount"
                                value={money(
                                    data.anticipated_amount
                                )}
                            />

                            <Field
                                label="Available Funds"
                                value={money(
                                    data.available_funds
                                )}
                            />

                            <Field
                                label="Interest Rate"
                                value={
                                    data.interest_rate !== null &&
                                    data.interest_rate !== undefined
                                        ? `${data.interest_rate}%`
                                        : "-"
                                }
                            />

                            <Field
                                label="Loan Term"
                                value={data.loan_term}
                            />

                            <Field
                                label="Monthly Payment"
                                value={money(
                                    data.monthly_payment
                                )}
                            />

                            <Field
                                label="Rate Type"
                                value={data.rate_type}
                            />

                        </div>
                    </PrintSection>

                    {/* =========================================
                        CASH FLOW
                    ========================================= */}

                    <PrintSection
                        title="Cash Flow & Financial Capacity"
                        subtitle="Membership, income, expenses and reserve funds"
                        icon={<WalletOutlined />}
                    >
                        <div className="field-grid">

                            <Field
                                label="Adult Membership"
                                value={data.adult_membership}
                            />

                            <Field
                                label="Tithe Paying Members"
                                value={data.tithe_paying_members}
                            />

                            <Field
                                label="Average Monthly Tithes"
                                value={money(
                                    data.average_monthly_tithes
                                )}
                            />

                            <Field
                                label="Average Monthly Expense"
                                value={money(
                                    data.average_monthly_expense
                                )}
                            />

                            <Field
                                label="Monthly Remittance"
                                value={money(
                                    data.average_monthly_remittance
                                )}
                            />

                            <Field
                                label="Amount in Coffers / LDF"
                                value={money(
                                    data.amount_in_coffers_ldf
                                )}
                            />

                            <Field
                                label="Emergency Fund"
                                value={money(
                                    data.emergency_fund_available
                                )}
                            />

                            <Field
                                label="Building Fund"
                                value={money(
                                    data.building_fund_available
                                )}
                            />

                            <Field
                                label="Combined Available Funds"
                                value={money(available)}
                                strong
                            />

                        </div>
                    </PrintSection>

                    {/* =========================================
                        FUTURE COST
                    ========================================= */}

                    <PrintSection
                        title="Future Costs"
                        subtitle="Expected future financial obligations"
                        icon={<DollarOutlined />}
                    >
                        <JsonGrid
                            data={futureCosts}
                            moneyKeys={Object.keys(futureCosts)}
                        />
                    </PrintSection>

                    {/* =========================================
                        INCOME
                    ========================================= */}

                    <PrintSection
                        title="Income Potential"
                        subtitle="Expected income and financial benefit"
                        icon={<BankOutlined />}
                    >
                        <JsonGrid data={income} />
                    </PrintSection>

                    {/* =========================================
                        READINESS
                    ========================================= */}

                    <PrintSection
                        title="Financial Readiness Checklist"
                        subtitle="Financial readiness and supporting assessment"
                        icon={<CheckCircleOutlined />}
                    >
                        <JsonGrid data={checklist} />
                    </PrintSection>

                    {/* =========================================
                        RISK
                    ========================================= */}

                    <PrintSection
                        title="Risk Assessment"
                        subtitle="Risk factors identified for this request"
                        icon={<SafetyCertificateOutlined />}
                    >
                        <JsonGrid data={risks} />
                    </PrintSection>

                    {/* =========================================
                        DOCUMENTS
                    ========================================= */}

                    <PrintSection
                        title={`Supporting Documents (${documents.length})`}
                        subtitle="Documents submitted with the request"
                        icon={<FileTextOutlined />}
                    >
                        {documents.length ? (
                            <table className="document-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 35 }}>
                                            #
                                        </th>

                                        <th>
                                            Document
                                        </th>

                                        <th style={{ width: 150 }}>
                                            Type
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {documents.map((doc, index) => (
                                        <tr key={doc.id}>
                                            <td>
                                                {index + 1}
                                            </td>

                                            <td>
                                                {doc.original_file_name ||
                                                    doc.file_name ||
                                                    "-"}
                                            </td>

                                            <td>
                                                {pretty(
                                                    doc.document_type
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="empty-info">
                                No supporting documents attached.
                            </div>
                        )}
                    </PrintSection>

                    {/* =========================================
                        WORKFLOW
                    ========================================= */}

                    <PrintSection
                        title="Approval & Workflow History"
                        subtitle="Official review and approval trail"
                        icon={<ClockCircleOutlined />}
                    >
                        {workflow.length ? (
                            <div className="workflow-list">

                                {workflow.map((item) => (
                                    <div
                                        className="workflow-item"
                                        key={item.id}
                                    >

                                        <div className="workflow-dot">
                                            <CheckCircleOutlined />
                                        </div>

                                        <div className="workflow-main">

                                            <div className="workflow-title">

                                                {pretty(item.action)}

                                                <span className="workflow-level">
                                                    •{" "}
                                                    {LEVEL_LABELS[
                                                        normalize(
                                                            item.level
                                                        )
                                                    ] ||
                                                        display(
                                                            item.level
                                                        )}

                                                    {item.to_level
                                                        ? ` → ${
    LEVEL_LABELS[
        normalize(
            item.to_level
        )
        ] ||
    item.to_level
}`
                                                        : ""}
                                                </span>

                                            </div>

                                            {item.feedback ? (
                                                <div className="workflow-comment">
                                                    {item.feedback}
                                                </div>
                                            ) : null}

                                            {item.comments ? (
                                                <div className="workflow-comment">
                                                    {item.comments}
                                                </div>
                                            ) : null}

                                            {item.reason ? (
                                                <div
                                                    className="workflow-comment"
                                                    style={{
                                                        color:
                                                            COLORS.red
                                                    }}
                                                >
                                                    Reason:{" "}
                                                    {item.reason}
                                                </div>
                                            ) : null}

                                        </div>

                                        <div className="workflow-user">

                                            <div className="workflow-user-name">
                                                {item.action_by_name ||
                                                    item.user_name ||
                                                    (item.action_by
                                                        ? `User #${item.action_by}`
                                                        : "-")}
                                            </div>

                                            <div className="workflow-date">
                                                {item.action_at
                                                    ? dayjs(
                                                          item.action_at
                                                      ).format(
                                                          "DD MMM YYYY, hh:mm A"
                                                      )
                                                    : "-"}
                                            </div>

                                        </div>

                                    </div>
                                ))}

                            </div>
                        ) : (
                            <div className="empty-info">
                                No workflow history available.
                            </div>
                        )}
                    </PrintSection>

                    {/* =========================================
                        AUDIT
                    ========================================= */}

                    <PrintSection
                        title="Audit Information"
                        subtitle="Record creation and modification information"
                        icon={<UserOutlined />}
                    >
                        <div className="field-grid">

                            <Field
                                label="Created By"
                                value={
                                    data.created_by_name ||
                                    data.creator_name ||
                                    (data.created_by
                                        ? `User #${data.created_by}`
                                        : "-")
                                }
                            />

                            <Field
                                label="Created At"
                                value={
                                    data.created_at
                                        ? dayjs(
                                              data.created_at
                                          ).format(
                                              "DD MMM YYYY, hh:mm A"
                                          )
                                        : "-"
                                }
                            />

                            <Field
                                label="Updated By"
                                value={
                                    data.updated_by_name ||
                                    data.updater_name ||
                                    (data.updated_by
                                        ? `User #${data.updated_by}`
                                        : "-")
                                }
                            />

                            <Field
                                label="Updated At"
                                value={
                                    data.updated_at
                                        ? dayjs(
                                              data.updated_at
                                          ).format(
                                              "DD MMM YYYY, hh:mm A"
                                          )
                                        : "-"
                                }
                            />

                            <Field
                                label="Current Status"
                                value={pretty(data.status)}
                                strong
                            />

                            <Field
                                label="Current Approval Level"
                                value={
                                    LEVEL_LABELS[
                                        normalize(
                                            data.current_level
                                        )
                                    ] ||
                                    display(data.current_level)
                                }
                            />

                        </div>
                    </PrintSection>

                    {/* =========================================
                        APPROVAL SIGNATURES
                    ========================================= */}

                    <PrintSection
                        title="Authorization"
                        subtitle="Approval and authorization record"
                        icon={<CheckCircleOutlined />}
                    >
                        <div className="signature-grid">

                            <div className="signature">
                                <div className="signature-line">
                                    <div className="signature-role">
                                        District Assessment
                                    </div>

                                    <div className="signature-help">
                                        Name / Signature / Date
                                    </div>
                                </div>
                            </div>

                            <div className="signature">
                                <div className="signature-line">
                                    <div className="signature-role">
                                        Area Assessment
                                    </div>

                                    <div className="signature-help">
                                        Name / Signature / Date
                                    </div>
                                </div>
                            </div>

                            <div className="signature">
                                <div className="signature-line">
                                    <div className="signature-role">
                                        National Approval
                                    </div>

                                    <div className="signature-help">
                                        Name / Signature / Date
                                    </div>
                                </div>
                            </div>

                        </div>
                    </PrintSection>

                    {/* =========================================
                        FOOTER
                    ========================================= */}

                    <footer className="report-footer">

                        <div>
                            Generated:{" "}
                            {dayjs().format(
                                "DD MMM YYYY, hh:mm A"
                            )}
                        </div>

                        <div className="confidential">
                            Asset Request • Official Record
                        </div>

                        <div>
                            Ref:{" "}
                            {data.request_no ||
                                `AR-${data.id}`}
                        </div>

                    </footer>

                </div>

            </main>

        </div>
    );
}
